import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./SectionsDrawerItem.module.css";
import CompletionStatus from "../Utils/CompletionStatus/CompletionStatus";
import {
  IDrawerItem,
  IDrawerSection,
} from "../../Types/Drawer/ISectionsDrawerItem";
import ContextMenu from "../Utils/ContextMenu/ContextMenu";
import { ContextMenuItem } from "../../Types/ContextMenuItem";
import Checkbox from "../Utils/Checkbox/Checkbox";
import GradeDisplay from "../Utils/GradeDisplay/GradeDisplay";
import { selectRolePermissions } from "../../Redux/Store/Slices/questionnaire";
import { useAppSelector } from "../../Redux/Store/Store";
import Tooltip from "../Utils/Tooltip/Tooltip";
import { ProductType } from "../../Types/ProductType";

const brandColours: Record<number, string> = {
  [ProductType.Evaluate]: "#E25A74",
  [ProductType.Enquire]: "#E25A74",
  [ProductType.Improve]: "#52BFA2",
  [ProductType.Develop]: "#EAB33C",
};

type Props = {
  section: IDrawerSection;
  basePath: string;
  activeSection: boolean;
  /** When true, criteria rows are shown (active section and/or expand-all in parent). */
  criteriaExpanded: boolean;
  onCheckBoxChanged?: (item: IDrawerItem, checked: boolean) => void;
  activeItemId?: string;
  onClickHandler?: (item: IDrawerItem) => void;
  /** Called when user navigates (section or item link click). Use to e.g. close mobile overlay. */
  onNavigate?: () => void;
  sectionsOnly?: boolean;
  contextMenuItems?: ContextMenuItem[];
  contextMenuPosition?: "left" | "right";
};

const SectionsDrawerItem: React.FC<Props> = ({
  section,
  basePath,
  activeSection,
  criteriaExpanded,
  activeItemId,
  onClickHandler,
  onNavigate,
  sectionsOnly,
  contextMenuItems,
  contextMenuPosition,
  onCheckBoxChanged,
}) => {
  const items = section.items ?? [];
  const hasItems = items.length > 0;
  const [meatballsEnabled, setMeatballsEnabled] = useState(false);

  const rolePermissions = useAppSelector(selectRolePermissions());
  const productType = useAppSelector((state) => state.questionnaire.questionnaire?.productType);
  const accentColour = brandColours[productType ?? -1] ?? "#52BFA2";

  const isIntro = section.isIntro === true;
  const isAction = section.isAction === true;
  const hasDividerBefore = section.dividerBefore === true;

  useEffect(() => {
    if (!section.items) {
      console.error("Section.items is undefined:", section);
    } else if (items.length === 0) {
      console.warn(
        "Section has zero items (will render header only):",
        section
      );
    }
  }, [section, items]);

  // where should clicking this header go?
  const linkUrl = hasItems
    ? basePath + items[0].url
    : `${basePath}/${section.id}`;

  const showCriteriaList = criteriaExpanded && !sectionsOnly && hasItems;

  const containerClassName =
    `${styles.container}` +
    ` ${activeSection ? styles.active : ""}` +
    ` ${isIntro ? styles.introScope : ""}` +
    ` ${isAction ? styles.actionItem : ""}` +
    ` ${hasDividerBefore ? styles.dividerBefore : ""}` +
    ` ${section.searchRelevance === "high" ? styles.searchGlow : ""}`;

  const titleRow = (
    <div
      className={styles.titleContainer}
      onMouseOver={() => setMeatballsEnabled(true)}
      onMouseLeave={() => setMeatballsEnabled(false)}
    >
      <h3 style={sectionsOnly ? { fontSize: "small" } : undefined}>
        {section.title}
      </h3>

      {contextMenuItems && (
        <ContextMenu
          items={contextMenuItems}
          contextId={section.id}
          position={contextMenuPosition}
        >
          {(meatballsEnabled || activeSection) && (
            <div className={styles.menu}>
              <img src="/img/menu-meatballs.svg" alt="menu" />
            </div>
          )}
        </ContextMenu>
      )}

      {!sectionsOnly && section.status !== "Not Set" && (
        <CompletionStatus status={section.status} />
      )}
    </div>
  );

  const criteriaBlock = showCriteriaList ? (
    <div className={styles.criteriaItems}>
      {items.map((item) => {
        const isActive = item.id === activeItemId;
        return (
          <div
            key={item.id}
            className={`${styles.itemContainer} ${isActive ? styles.activeItemContainer : ""}`}
          >
            <div className={styles.itemContent}>
              <Link
                to={basePath + item.url}
                onClick={() => {
                  onClickHandler?.(item);
                  onNavigate?.();
                }}
              >
                <p
                  className={styles.item}
                  style={{
                    fontWeight: isActive ? 700 : 500,
                    padding: "7px 0",
                  }}
                >
                  {item.title}
                </p>
              </Link>
            </div>
            <div className={styles.gradeAndCheckbox}>
              {item.grade && item.grade !== "Not Set" && (
                <GradeDisplay grade={item.grade} size="small" />
              )}
              {onCheckBoxChanged && (
                <Tooltip
                  text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the completion status`}
                  align={"top"}
                  sideAlign={"left"}
                  disabled={rolePermissions?.editDate}
                >
                  <Checkbox
                    clickHandler={(checked) => onCheckBoxChanged(item, checked)}
                    value={item.checked}
                    disabled={!rolePermissions?.editDate}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        );
      })}
    </div>
  ) : null;

  if (activeSection) {
    return (
      <div className={styles.sectionOuter}>
        <div
          className={containerClassName}
          style={activeSection ? { borderLeftColor: accentColour } : undefined}
        >
          {titleRow}
          {criteriaBlock}
        </div>
      </div>
    );
  }

  if (showCriteriaList) {
    return (
      <div className={styles.sectionOuter}>
        <div className={`${containerClassName} ${styles.expandedInactive}`}>
          <Link to={linkUrl} onClick={onNavigate} className={styles.inactiveExpandedTitleLink}>
            {titleRow}
          </Link>
          {criteriaBlock}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sectionOuter}>
      <Link to={linkUrl} onClick={onNavigate} className={styles.inactiveHeaderLink}>
        <div className={containerClassName}>{titleRow}</div>
      </Link>
    </div>
  );
};

export default SectionsDrawerItem;
